import type { KeyboardEvent, MouseEvent } from 'react';
import type { NotificationRow as Row } from './notificationGroups';
import { iconForKind, toneOf } from './notificationLook';

interface NotificationRowProps {
  row: Row;
  /** Marks it read and navigates. Given the notification so the caller can use the server's own link. */
  onOpen: (row: Row) => void;
}

/**
 * One notification: icon, title, time, description, reference chip, action button, unread dot.
 *
 * <p>A `div` with a button role rather than a `<button>`, because the row contains the action button and
 * HTML forbids a button inside a button — a nested one is dropped by the parser, so the action would
 * simply not render. The role, tabIndex and key handling put back what the element gives for free.
 */
export function NotificationRow({ row, onOpen }: NotificationRowProps) {
  const { notification, time } = row;
  const openable = notification.link !== null;

  const open = () => {
    if (openable) onOpen(row);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      open();
    }
  };

  const onActionClick = (event: MouseEvent<HTMLButtonElement>) => {
    /* Or the row's own handler fires as well and the notification is opened twice. */
    event.stopPropagation();
    open();
  };

  return (
    <div
      className={`header__notify-row${notification.read ? '' : ' header__notify-row--unread'}`}
      /*
        A notification that points somewhere is a way to get there. One that points nowhere is only worth
        reading, and stays inert rather than offering a click that does nothing.
      */
      role={openable ? 'button' : undefined}
      tabIndex={openable ? 0 : undefined}
      onClick={open}
      onKeyDown={openable ? onKeyDown : undefined}
    >
      <span className={`header__notify-icon ${toneOf(notification.severity)}`} aria-hidden="true">
        <i className={`pi ${iconForKind(notification.kind)}`} />
      </span>

      <span className="header__notify-text">
        {/* Said rather than shown in a dot, so the state reaches a screen reader as part of the row. */}
        {notification.read ? null : <span className="visually-hidden">Unread. </span>}
        <span className="header__notify-line">
          <span className="header__notify-title">{notification.title}</span>
          <span className="header__notify-time">{time}</span>
        </span>

        {notification.body === null ? null : (
          <span className="header__notify-desc">{notification.body}</span>
        )}

        {notification.reference === null && notification.actionLabel === null ? null : (
          <span className="header__notify-meta">
            {notification.reference === null ? null : (
              <span className="header__notify-ref">{notification.reference}</span>
            )}
            {notification.actionLabel === null ? null : (
              <button type="button" className="header__notify-action" onClick={onActionClick}>
                {notification.actionLabel}
              </button>
            )}
          </span>
        )}
      </span>

      {/* The unread marker. Hidden from assistive technology: the row already says "Unread" above. */}
      <span
        className={`header__notify-dot${notification.read ? '' : ' header__notify-dot--unread'}`}
        aria-hidden="true"
      />
    </div>
  );
}
